/* eslint-disable react/jsx-indent */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Article from '../items/Article';
import { useResizeObserver } from '../../hooks/useObserver';
import { useWindowSize } from '../../contexts/SiteContext';

import * as AppPropTypes from '../../lib/PropTypes';

import styles from '../../../styles/lists/articles.module.scss';

const propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({})),
    allLoaded: PropTypes.bool,
    loadNextPage: PropTypes.func,
    loading: PropTypes.bool,
    page: AppPropTypes.page,
    currentSlug: PropTypes.string,
    onClick: PropTypes.func,
    margin: PropTypes.number,
    scrollToClick: PropTypes.bool,
    className: PropTypes.string,
};

const defaultProps = {
    items: null,
    allLoaded: false,
    loadNextPage: null,
    loading: false,
    page: null,
    currentSlug: null,
    onClick: null,
    margin: 0,
    scrollToClick: false,
    className: null,
};

const Articles = ({ items, allLoaded, loadNextPage, loading, page, margin, currentSlug, onClick, scrollToClick, className }) => {
    const { height: windowHeight = 0 } = useWindowSize();
    const [index, setIndex] = useState(0);

    // Measure
    const {
        ref: itemsRef,
        entry: { contentRect: itemsContentRect = null },
    } = useResizeObserver();
    const { height: itemsHeight = 0 } = itemsContentRect || {};

    const {
        ref: itemRef,
        entry: { contentRect: itemContentRect = null },
    } = useResizeObserver();
    const { height: itemHeight = 0 } = itemContentRect || {};

    const { length } = items || [];

    const currentItem = (items || []).reduce((c, it) => {
        const { slug: itemSlug = null } = it || {};
        const current = itemSlug === currentSlug;
        if (current) {
            return it;
        }
        return c;
    }, page);

    const count = useMemo(() => {
        if (itemsHeight > 0 && itemHeight > 0) {
            return Math.max(Math.floor((itemsHeight - 10 + margin) / itemHeight), 2);
        }
        return 4;
    }, [windowHeight, length, currentItem, itemHeight, margin]);
    const minimum = length - count;

    const filteredItems = (items || []).reduce((all, it, idx) => {
        all.push(it);
        return all;
    }, []);

    // Trigger a load
    useEffect(() => {
        if (minimum > 0 && index >= minimum - 1 && !allLoaded && !loading) {
            loadNextPage();
        }
    }, [index, allLoaded, loadNextPage, minimum, loading]);

    const onScroll = useCallback(
        (e) => {
            const atSnappingPoint = e.target.scrollTop % e.target.offsetHeight === 0;
            const timeOut = atSnappingPoint ? 0 : 150; // see notes

            clearTimeout(e.target.scrollTimeout); // clear previous timeout

            e.target.scrollTimeout = setTimeout(() => {
                const newIndex = Math.floor(e.target.scrollTop / 150);
                setIndex(newIndex);
            }, timeOut);
        },
        [itemsRef.current],
    );

    useEffect(() => {
        if (itemsRef.current !== null) {
            itemsRef.current.addEventListener('scroll', onScroll);
        }
        return () => {
            if (itemsRef.current !== null) {
                itemsRef.current.removeEventListener('scroll', onScroll);
            }
        };
    }, [itemsRef.current, onScroll]);

    const onClickItem = useCallback(
        (e, idx = null) => {
            // if (e.target) {
            //     e.target.focus();
            // }
            if (idx !== null && scrollToClick) {
                itemsRef.current.scroll({
                    behavior: 'smooth',
                    top: idx * 150 - 3,
                });
            }

            if (onClick !== null) {
                onClick(e);
            }
        },
        [onClick, itemsRef.current, scrollToClick],
    );

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
        >
            <div className={styles.items} ref={itemsRef}>
                {filteredItems !== null
                    ? filteredItems.map((item, itemIndex) => (
                          <div
                              key={`item-${itemIndex + 1}`}
                              ref={itemIndex === 0 ? itemRef : null}
                              className={styles.articleWrapper}
                          >
                              <Article
                                  item={item}
                                  index={itemIndex}
                                  active={item !== null && currentSlug === item.slug}
                                  onClick={onClickItem}
                              />
                          </div>
                      ))
                    : null}
            </div>
        </div>
    );
};

Articles.propTypes = propTypes;
Articles.defaultProps = defaultProps;

export default Articles;
